import styled from 'styled-components';
import SearchIcon from '@mui/icons-material/Search';

import { useParams } from 'react-router-dom';
import { Box, InputAdornment, Stack, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { Clear, PlagiarismOutlined } from '@mui/icons-material';
import { useTheme } from '../../../../../styles/theme';
import { UseMutateAsyncFunction, useQuery } from '@tanstack/react-query';
import { getBuckets } from '@requests/buckets/buckets.requests';
import { v4 as uuidv4 } from 'uuid';
import { Button, LoadingButton } from '@common/Button';
import { useSnackbar } from 'notistack';
import { bucketActions } from '@store/slices';
import { useDispatch, useSelector } from 'react-redux';
import { State } from '@store/index';
import { Card } from '@common/Card';
import { BucketInput } from '@common/BucketInput/BucketInput';
import { Option } from '@common/BucketInput/BucketField';
import { GetBucketsParams, SearchBucketParams, TelnyxBucket } from '@requests/buckets';
import { RequestError } from '@requests/common';

const WrapText = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  padding-top: 0.25rem;

  @media (max-width: 1400px) {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }
`;

interface Props {
  isQueryLoading: boolean;
  allowRead: boolean;
  sendQuery: UseMutateAsyncFunction<any, Error, SearchBucketParams, void>;
}

export const BucketSearch: React.FC<Props> = ({ isQueryLoading, sendQuery, allowRead }) => {
  const dispatch = useDispatch();

  const { service_id } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const theme = useTheme();

  const prev_query = useSelector((state: State) => state.buckets.search.query);
  const min_certainty = useSelector((state: State) => state.buckets.search.min_certainty);
  const max_results = useSelector((state: State) => state.buckets.search.max_results);
  const indexes = useSelector((state: State) => state.buckets.search.indexes);
  const results = useSelector((state: State) => state.buckets.search.results);

  const [query, setQuery] = useState(prev_query || '');
  const [maxResults, setMaxResults] = useState(max_results || 3);
  const [minCertainty, setMinCertainty] = useState(min_certainty || 70);

  const [options, setOptions] = useState<Option[]>([
    { label: 'Support Docs', value: 'Support_docs', type: 'weaviate' },
    { label: 'Dotcom Docs', value: 'Dotcom_docs', type: 'weaviate' },
    { label: 'Developer Docs', value: 'Developer_docs', type: 'weaviate' },
    { label: 'API Docs', value: 'Api_docs', type: 'weaviate' },
  ]);

  const { data: buckets = [], status } = useQuery<
    TelnyxBucket[],
    RequestError,
    TelnyxBucket[],
    [string, string, GetBucketsParams]
  >({
    queryKey: ['globalFiles', 'buckets', { service_id: service_id as string }],
    queryFn: getBuckets,
  });

  useEffect(() => {
    if (status === 'success' && Array.isArray(buckets)) {
      for (const bucket of buckets) {
        if (!options?.some((option) => option.value === bucket.name))
          setOptions((prevState) =>
            prevState.concat({
              label: bucket.name,
              value: bucket.name,
              type: 'telnyx',
            })
          );
      }
    }
  }, [buckets, status]);

  const send = () => {
    if (!query) {
      return enqueueSnackbar('No search query provided!', {
        variant: 'error',
        autoHideDuration: 6000,
      });
    }

    enqueueSnackbar('Sending request...', { variant: 'info', autoHideDuration: 3000 });
    sendQuery({
      question: query,
      indexes: indexes
        .filter((idx) => idx.index)
        .map((idx) => ({ index: idx.index, weight: idx.weight, type: idx?.type })),
      max_results,
      min_certainty,
    });
    dispatch(bucketActions.setBucketSearch({ field: 'query', value: query }));
  };

  const isMaxBuckets = indexes.length >= options.length;

  const addIndex = () => {
    if (isMaxBuckets) return;

    const value = { id: uuidv4(), index: '', weight: 1, type: null };
    dispatch(bucketActions.addBucketSearchIndex(value));
  };

  const updateIndexField = (indexField: string, indexFieldId: string, indexValue: string, indexType: string) => {
    dispatch(
      bucketActions.setBucketSearch({ field: indexField, value: indexValue, id: indexFieldId, type: indexType })
    );
  };

  const body = (
    <Stack spacing={2} padding='0.5rem 0'>
      <TextField
        variant='outlined'
        size='small'
        fullWidth
        label='Search Query'
        value={query}
        multiline
        minRows={2}
        maxRows={4}
        onChange={(e) => {
          const value = e.target.value;

          setQuery(value);
          dispatch(bucketActions.setBucketSearch({ field: 'query', value }));
        }}
      />
      <WrapText>
        <TextField
          variant='outlined'
          size='small'
          label='Max Results'
          type='number'
          value={maxResults}
          placeholder='10'
          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setMaxResults(Number.parseInt(e.target.value));
            dispatch(bucketActions.setMaxResults(Number.parseInt(e.target.value)));
          }}
        />
        <TextField
          variant='outlined'
          size='small'
          label='Min Certainty'
          value={minCertainty}
          type='number'
          InputProps={{
            endAdornment: <InputAdornment position='end'>%</InputAdornment>,
          }}
          placeholder='90'
          onChange={(e) => {
            setMinCertainty(Number.parseInt(e.target.value));
            dispatch(bucketActions.setMinCertainty(Number.parseInt(e.target.value)));
          }}
        />
      </WrapText>
      <BucketInput
        indexes={indexes}
        addIndex={addIndex}
        updateField={updateIndexField}
        removeIndex={(value) => dispatch(bucketActions.removeBucketSearchIndex(value))}
      />
    </Stack>
  );

  const actions = (
    <Stack display='grid' gridTemplateColumns={results.length ? '6fr 4fr' : '1fr'} gap='8px'>
      {results.length > 0 && (
        <Button
          fullWidth
          onClick={() => dispatch(bucketActions.storeBucketSearchResults([]))}
          startIcon={isMaxBuckets ? null : <Clear sx={{ fontSize: '2ch !important' }} />}
        >
          Clear Results
        </Button>
      )}
      <LoadingButton
        fullWidth
        disabled={indexes.filter((item) => item.index).length <= 0 || !query}
        onClick={send}
        loading={isQueryLoading}
        customColor='primary'
        minHeight='32px'
        startIcon={isMaxBuckets ? null : <SearchIcon sx={{ fontSize: '2ch !important' }} />}
      >
        Search
      </LoadingButton>
    </Stack>
  );

  if (!allowRead) return null;

  return (
    <Box padding='12px'>
      <Card
        body={body}
        actions={indexes.length ? actions : undefined}
        title='Similarity Search'
        descriptionBorder
        icon={<PlagiarismOutlined sx={{ color: theme.primary, fontSize: '2.2ch' }} />}
        tooltip='Perform similarity search on one or more buckets/indexes. The weights can be used to give a particular index a higher certainty. The "Global Indexes" in the dropdown represent the custom vectorstores that NOC ENG setup and are available to all users of the flow tool.'
        description='Only embedded buckets can be searched.'
      />
    </Box>
  );
};
